/* vr-header.js — desktop mega panels, inline search, mobile drawer, mini-bag.
   Vanilla ES6, no jQuery, no Atlantic coupling (the section emits no
   data-section-type, so atlantic.js never touches this markup).
   Document-level listeners (Escape, click-outside, editor events) are attached
   ONCE behind the guard and resolve the current .vr-header at event time;
   per-element listeners are attached in init() and re-attached on
   shopify:section:load, when the editor re-renders the section. */
(() => {
  if (window.__vrHeader) return;
  window.__vrHeader = true;

  const DESKTOP = '(min-width: 960px)';
  const NO_HOVER = '(hover: none)';
  const HOVER_MS = 100;            /* hover intent, both directions */

  const root = () => document.querySelector('.vr-header');
  const byId = (id) => (id ? document.getElementById(id) : null);

  /* ---------- desktop mega panels ---------- */
  const closePanel = (item) => {
    item.classList.remove('is-open');
    const link = item.querySelector('.vr-header__link');
    if (link) link.setAttribute('aria-expanded', 'false');
  };
  const closePanels = (hdr) => hdr && hdr.querySelectorAll('.vr-header__item.is-open').forEach(closePanel);
  const openPanel = (item) => {
    closePanels(item.closest('.vr-header'));
    item.classList.add('is-open');
    const link = item.querySelector('.vr-header__link');
    if (link) link.setAttribute('aria-expanded', 'true');
  };

  /* ---------- mobile drawer ---------- */
  const placeDrawer = (hdr) => {
    /* The drawer is fixed; it starts where the bar ends on screen, so it sits
       under the announcement bar at the top of the page, under the sticky bar
       once scrolled, and under a non-sticky bar wherever that is. */
    const drawer = hdr.querySelector('[data-vr-drawer]');
    const bar = hdr.querySelector('.vr-header__bar');
    if (!drawer || !bar) return;
    drawer.style.top = Math.max(0, bar.getBoundingClientRect().bottom) + 'px';
  };
  /* Sub menu: a second screen that replaces the tab's rows, not a dropdown. The open state lives
     on the tabpanel and the drawer's inner box so CSS can hide the rows, promo and account block. */
  const closeSub = (hdr, refocus) => {
    const open = hdr && hdr.querySelector('[data-vr-subpanel]:not([hidden])');
    if (!open) return;
    open.hidden = true;
    const panel = open.closest('[data-vr-tabpanel]');
    if (panel) panel.classList.remove('is-sub-open');
    const inner = hdr.querySelector('.vr-header__drawer-inner');
    if (inner) inner.classList.remove('is-sub-open');
    const toggle = hdr.querySelector('[data-vr-row-toggle][aria-controls="' + open.id + '"]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      if (refocus) toggle.focus();
    }
  };
  const openSub = (hdr, btn, refocus) => {
    const sub = byId(btn.getAttribute('aria-controls'));
    if (!sub) return;
    closeSub(hdr, false);
    sub.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    const panel = sub.closest('[data-vr-tabpanel]');
    if (panel) panel.classList.add('is-sub-open');
    const inner = hdr.querySelector('.vr-header__drawer-inner');
    if (inner) inner.classList.add('is-sub-open');
    const drawer = hdr.querySelector('[data-vr-drawer]');
    if (drawer) drawer.scrollTop = 0;
    const back = sub.querySelector('[data-vr-sub-back]');
    if (back && refocus) back.focus();
  };

  const closeDrawer = (hdr) => {
    const drawer = hdr && hdr.querySelector('[data-vr-drawer]');
    if (!drawer || drawer.hidden) return;
    closeSub(hdr, false);
    drawer.hidden = true;
    hdr.classList.remove('is-drawer-open');
    document.documentElement.classList.remove('vr-nav-open');
    const btn = hdr.querySelector('[data-vr-drawer-toggle]');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  };

  /* ---------- search ---------- */
  const closeSearch = (hdr, refocus) => {
    const form = hdr && hdr.querySelector('[data-vr-search-form]');
    if (!form || form.hidden) return;
    form.hidden = true;
    hdr.classList.remove('is-search-open');
    const toggle = hdr.querySelector('[data-vr-search-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      if (refocus) toggle.focus();
    }
  };
  const openSearch = (hdr) => {
    const form = hdr.querySelector('[data-vr-search-form]');
    if (!form) return;
    closePanels(hdr);
    closeDrawer(hdr);
    form.hidden = false;
    hdr.classList.add('is-search-open');
    const toggle = hdr.querySelector('[data-vr-search-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    const input = form.querySelector('[data-vr-search-input]');
    if (input) input.focus();
  };

  const openDrawer = (hdr) => {
    const drawer = hdr.querySelector('[data-vr-drawer]');
    if (!drawer) return;
    closeSearch(hdr, false);
    placeDrawer(hdr);
    drawer.hidden = false;
    hdr.classList.add('is-drawer-open');
    document.documentElement.classList.add('vr-nav-open');
    const btn = hdr.querySelector('[data-vr-drawer-toggle]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
  };

  const selectTab = (hdr, tab) => {
    if (!tab) return;
    closeSub(hdr, false);
    hdr.querySelectorAll('[data-vr-tab]').forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      const panel = byId(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  };

  /* ---------- mini-bag (Atlantic's sidebar drawer) ----------
     Atlantic's CartMini/SidebarDrawer are closure-private, and its header class
     cannot be shimmed (its Navigation constructor dereferences nav.full and would
     throw inside the section-registration loop). The drawer itself is plain DOM in
     layout/theme.liquid, driven by two attributes that theme.css keys on, and its
     contents come from the cart.mini view. So this opens the same drawer the same
     way: same attributes, same view, same scroll-lock class. Atlantic's own
     instance still handles the drawer it opens after an add-to-cart; both sets of
     close handlers run on the same X / backdrop / Escape and set the same values,
     so neither can strand the other. Item removal is handled here only for a
     drawer this code opened (Atlantic registers its own remover when it opens). */
  const bag = {
    container: null, header: null, content: null, trigger: null, mine: false, busy: false,
  };
  const STATE = 'data-sidebar-drawer-animation-state';
  const CHANGE = 'data-sidebar-drawer-animation';
  const cartUrl = () => (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';

  const bagSetup = () => {
    if (bag.container) return true;
    const c = document.querySelector('[data-sidebar-drawer-container]');
    if (!c) return false;
    bag.container = c;
    bag.header = c.querySelector('[data-sidebar-drawer-header]');
    bag.content = c.querySelector('[data-sidebar-drawer-content]');
    const close = c.querySelector('[data-sidebar-drawer-close]');
    if (close) close.addEventListener('click', () => bagClose());
    c.addEventListener('click', (e) => {
      if (e.target === c) { bagClose(); return; }
      const rm = e.target.closest('[data-cart-mini-item-remove]');
      if (rm && bag.mine) { e.preventDefault(); bagRemove(rm); }
    });
    return true;
  };

  const bagFetch = () => fetch(cartUrl() + '?view=mini&_=' + Date.now(), { credentials: 'same-origin' })
    .then((r) => r.text())
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const part = (name) => { const t = doc.querySelector('template[data-html="' + name + '"]'); return t ? t.innerHTML : ''; };
      return { header: part('header'), content: part('content') };
    });

  const bagAnimate = (to) => {
    const c = bag.container;
    const from = c.getAttribute(STATE) || 'closed';
    if (from === to) { c.removeAttribute(CHANGE); return; }
    c.setAttribute(CHANGE, from + '=>' + to);      /* display:block for the transition */
    void c.offsetWidth;                            /* commit before the state flips */
    c.setAttribute(STATE, to);
    const done = (e) => { if (e.target !== c) return; c.removeEventListener('transitionend', done); if (c.getAttribute(STATE) === to) c.removeAttribute(CHANGE); };
    c.addEventListener('transitionend', done);
  };

  const bagOpen = (trigger) => {
    if (!bagSetup() || bag.busy) return;
    bag.busy = true;
    bagFetch().then((d) => {
      bag.header.innerHTML = d.header;
      bag.content.innerHTML = d.content;
      bag.mine = true;
      bag.trigger = trigger || document.activeElement;
      document.documentElement.classList.add('scroll-locked');
      bagAnimate('open');
      const close = bag.container.querySelector('[data-sidebar-drawer-close]');
      if (close) close.focus();
    }).catch(() => { window.location.href = cartUrl(); })
      .then(() => { bag.busy = false; });
  };

  const bagClose = () => {
    if (!bag.container || bag.container.getAttribute(STATE) !== 'open') return;
    bagAnimate('closed');
    document.documentElement.classList.remove('scroll-locked');
    if (bag.mine && bag.trigger && bag.trigger.focus) bag.trigger.focus();
    bag.mine = false;
    bag.trigger = null;
  };

  const bagRemove = (btn) => {
    const item = btn.closest('[data-cart-mini-item]');
    if (item) item.classList.add('cart-mini-item-loading');
    fetch(cartUrl() + '/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: btn.getAttribute('data-cart-mini-item-remove'), quantity: 0 }),
    }).then((r) => r.json()).then((cart) => {
      document.querySelectorAll('[data-cart-mini-count]').forEach((el) => { el.textContent = cart.item_count === 0 ? '' : String(cart.item_count); });
      if (document.querySelector('[data-section-id="static-cart"]')) { window.location.reload(); return; }
      return bagFetch().then((d) => { bag.header.innerHTML = d.header; bag.content.innerHTML = d.content; });
    }).catch(() => { if (item) item.classList.remove('cart-mini-item-loading'); });
  };

  const closeAll = (hdr) => {
    if (!hdr) return;
    closePanels(hdr);
    closeSearch(hdr, true);
    closeDrawer(hdr);
  };

  /* ---------- per-section wiring ---------- */
  const init = (hdr) => {
    if (!hdr || hdr.dataset.vrInit) return;
    hdr.dataset.vrInit = '1';

    const desktop = window.matchMedia(DESKTOP);

    hdr.querySelectorAll('.vr-header__item--has-panel').forEach((item) => {
      const link = item.querySelector('.vr-header__link');
      let timer = 0;
      item.addEventListener('mouseenter', () => {
        if (!desktop.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => openPanel(item), HOVER_MS);
      });
      item.addEventListener('mouseleave', () => {
        clearTimeout(timer);
        timer = setTimeout(() => closePanel(item), HOVER_MS);
      });
      item.addEventListener('focusin', () => { if (desktop.matches) openPanel(item); });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) closePanel(item); });
      if (link) {
        link.addEventListener('click', (e) => {
          /* Touch (no hover): first tap opens the panel, second follows the link. */
          if (window.matchMedia(NO_HOVER).matches && !item.classList.contains('is-open')) {
            e.preventDefault();
            openPanel(item);
          }
        });
      }
    });

    const searchToggle = hdr.querySelector('[data-vr-search-toggle]');
    if (searchToggle) {
      searchToggle.addEventListener('click', () => {
        const form = hdr.querySelector('[data-vr-search-form]');
        if (form && form.hidden) openSearch(hdr); else closeSearch(hdr, true);
      });
      const close = hdr.querySelector('[data-vr-search-close]');
      if (close) close.addEventListener('click', () => closeSearch(hdr, true));
    }

    const burger = hdr.querySelector('[data-vr-drawer-toggle]');
    if (burger) {
      burger.addEventListener('click', () => {
        const drawer = hdr.querySelector('[data-vr-drawer]');
        if (drawer && drawer.hidden) openDrawer(hdr); else closeDrawer(hdr);
      });
    }

    /* Bag: open the mini-bag when the drawer markup exists; the href stays /cart
       for no-JS, middle-click and any page without the drawer. */
    const cart = hdr.querySelector('.vr-header__cart');
    if (cart) {
      cart.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        if (!bagSetup()) return;
        e.preventDefault();
        closeAll(hdr);
        bagOpen(cart);
      });
    }

    hdr.querySelectorAll('[data-vr-tab]').forEach((tab) => {
      tab.addEventListener('click', () => selectTab(hdr, tab));
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const tabs = [...hdr.querySelectorAll('[data-vr-tab]')];
        const i = tabs.indexOf(tab);
        const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        selectTab(hdr, next);
        next.focus();
        e.preventDefault();
      });
    });

    hdr.querySelectorAll('[data-vr-row-toggle]').forEach((btn) => {
      /* Focus follows only a keyboard press (detail 0). After a tap, iOS Safari draws the focus
         ring on a programmatically focused button — the outlined box in issue #6. */
      btn.addEventListener('click', (e) => openSub(hdr, btn, e.detail === 0));
    });
    hdr.querySelectorAll('[data-vr-sub-back]').forEach((back) => {
      back.addEventListener('click', (e) => closeSub(hdr, e.detail === 0));
    });

    desktop.addEventListener('change', () => closeAll(hdr));
    window.addEventListener('resize', () => { if (hdr.classList.contains('is-drawer-open')) placeDrawer(hdr); });
  };

  /* ---------- document-level, once ---------- */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeAll(root());
    bagClose();
  });
  document.addEventListener('click', (e) => {
    const hdr = root();
    if (hdr && !hdr.contains(e.target)) { closePanels(hdr); closeSearch(hdr, false); }
  });

  /* The mini bag for other vr-* scripts: vr-product.js opens it after its own add-to-cart, the
     way Atlantic's form opens Atlantic's instance. Same drawer, same fetch, same scroll lock. */
  window.vrBag = { open: bagOpen, close: bagClose };

  /* Theme editor */
  document.addEventListener('shopify:section:load', (e) => {
    const hdr = e.target.querySelector('.vr-header');
    if (hdr) init(hdr);
  });
  document.addEventListener('shopify:section:unload', () => {
    document.documentElement.classList.remove('vr-nav-open');
  });
  document.addEventListener('shopify:block:select', (e) => {
    /* Show the promo being edited: its mega panel on desktop, its drawer tab on mobile. */
    const hdr = e.target.closest && e.target.closest('.vr-header');
    if (!hdr) return;
    if (window.matchMedia(DESKTOP).matches) {
      const item = e.target.closest('.vr-header__item');
      if (item) openPanel(item);
    } else {
      const panel = e.target.closest('[data-vr-tabpanel]');
      openDrawer(hdr);
      if (panel) selectTab(hdr, hdr.querySelector('[aria-controls="' + panel.id + '"]'));
    }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const hdr = e.target.closest && e.target.closest('.vr-header');
    if (hdr) closeAll(hdr);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(root()));
  } else {
    init(root());
  }
})();
